import { RootState } from '../'
import {
    setIsLoading, setLoadingMessage, setResults, setReportsResults, incrementConversationNo,
    setNlgParameterTransmissionResult,
    setIsNlgParameterTransmissionLoading,
    // setIsConversationOpen
} from '../features/transmissionSlice'

const BASE_URL = "https://eudtuapimgmd01.azure-api.net/transmission-failure/api/v1/"

interface ChatParams {
    user_prompt: string | null,
    conversation_no: number,
    conversation_id: string,
    test_report: string,
    user_email?: string
}

// Helper function to generate a unique conversation ID
const generateConversationId = () => {
    return 'conv_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
}

// Helper function to get headers
const getHeaders = (state: RootState, isFileUpload = false) => {
    const headers: Record<string, string> = {
        'Ocp-Apim-Subscription-Key': '949f1556f4cf4d969014bece6dcb2005'
    }

    if (!isFileUpload) {
        headers['Content-Type'] = 'application/json'
    } else {
        headers['Content-Type'] = 'multipart/form-data;boundary=SOME_BOUNDARY'
    }
    // For file uploads, don't set Content-Type - let the browser set it with boundary

    if (state.auth.idToken) {
        headers['Authorization'] = `Bearer ${state.auth.idToken}`
    }

    return headers
}

// Chat function using fetch API
export const chatWithTransmission = async (
    params: ChatParams,
    state: RootState,
    dispatch: any
) => {
    dispatch(setIsLoading(true))
    dispatch(setLoadingMessage("Processing your question..."))

    try {
        const response = await fetch(`${BASE_URL}data`, {
            method: 'POST',
            headers: getHeaders(state),
            body: JSON.stringify(params)
        })

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()

        dispatch(setResults({
            question: params.user_prompt,
            answer: data.response || data,
            type: 'chat',
            timestamp: new Date().toISOString()
        }))
        dispatch(setLoadingMessage(""))

        // Increment conversation number after successful chat
        dispatch(incrementConversationNo())
    } catch (err) {
        console.error("Error in chat:", err)
        dispatch(setResults({
            question: params.user_prompt,
            answer: "Sorry, I encountered an error processing your request.",
            type: 'error',
            timestamp: new Date().toISOString()
        }))
        dispatch(setLoadingMessage(""))
    } finally {
        dispatch(setIsLoading(false))
    }
}

// Chat function using fetch API
export const fetchFailedReportTransmission = async (
    params: ChatParams,
    state: RootState,
    dispatch: any
) => {
    dispatch(setIsLoading(true))
    dispatch(setLoadingMessage("Processing your question..."))

    try {
        const response = await fetch(`${BASE_URL}data`, {
            method: 'POST',
            headers: getHeaders(state),
            body: JSON.stringify(params)
        })

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()
        dispatch(setReportsResults(data[0].response_data))
        dispatch(setLoadingMessage(""))

        // Increment conversation number after successful chat
        dispatch(incrementConversationNo())
    } catch (err) {
        console.error("Error in chat:", err)
        dispatch(setReportsResults(null))
        dispatch(setLoadingMessage(""))
    } finally {
        // dispatch(setIsLoading(false))
        // dispatch(setIsConversationOpen(false))
    }
}

// Chat function using fetch API
export const nlgParameterTransmission = async (
    params: {
        failed_parameter: string,
        test_report: string,
        failed_test: string,
        value: number,
        minimum: number,
        maximum: number
    },
    state: RootState,
    dispatch: any
) => {
    dispatch(setIsNlgParameterTransmissionLoading(true))
    dispatch(setLoadingMessage("Processing your question..."))

    try {
        const response = await fetch(`${BASE_URL}nlg_parameter`, {
            method: 'POST',
            headers: getHeaders(state),
            body: JSON.stringify(params)
        })

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()

        dispatch(setNlgParameterTransmissionResult(data.responses))
    } catch (err) {
        console.error("Error in chat:", err)
        dispatch(setNlgParameterTransmissionResult(null))
        dispatch(setLoadingMessage(""))
    } finally {
        dispatch(setIsNlgParameterTransmissionLoading(false))
    }
}

// Upload function using fetch API
export const uploadFileWithTransmission = async (
    file: File,
    state: RootState,
    dispatch: any
) => {
    dispatch(setIsLoading(true))
    dispatch(setLoadingMessage("Uploading file..."))

    try {
        const formData = new FormData()
        formData.append('file', file)

        const response = await fetch(`${BASE_URL}upload`, {
            method: 'POST',
            headers: getHeaders(state, true), // true for file upload
            body: formData
        })

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
        }

        // Try to parse as JSON, but don't fail if it's not JSON
        let data
        const contentType = response.headers.get('content-type')
        if (contentType && contentType.includes('application/json')) {
            data = await response.json()
        } else {
            data = await response.text()
        }

        dispatch(setLoadingMessage("File uploaded successfully"))
        return data
    } catch (err) {
        console.error("Error uploading file:", err)
        dispatch(setLoadingMessage("Upload failed"))
        throw err
    } finally {
        dispatch(setIsLoading(false))
    }
}

// Export the helper function for use in components
export { generateConversationId } 